import { useCallback, useEffect, useRef, useState } from 'react';
import ReplayIcon from '@mui/icons-material/Replay';
import { Alert, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { Box } from '@mui/system';
import { useSnackbar } from 'notistack';
import { PDFJobRibbonProps } from './PDFJobRibbon.props';
import { ApiService, CompanyService, RetrieveDocumentGenerationJob } from '../../../../../api';
import { useStore } from '../../../../../common/store';
import { useAsyncProcessor } from '../../../../../context/AsyncProcessorContext/AsyncProcessorContext';
import { GenerationJobState } from '../../../../../pages/DocumentGenerationJobs/models/enums/generation-job-state.enum';
import { Logger } from '../../../../../utilities';
import { PARSING_PROGRESS_COPY, parsingProgressState } from '../adoptParsingJob';

export const PDFJobRibbon = ({
  generationJob,
  measurementDate,
  referenceObjectType = 'ct',
  onJobComplete,
  skipPolling,
  onRetrigger,
  parserReady = false,
  fileId,
}: PDFJobRibbonProps) => {
  const { addJob, removeJob } = useAsyncProcessor();
  const { enqueueSnackbar } = useSnackbar();
  const { apiCompanyDocumentGenerationJobsRead, apiCompanyDocumentGenerationJobsDocumentExecutionSessionsList } =
    ApiService;
  const [{ companyInfo }] = useStore();
  const [genJob, setGenJob] = useState(generationJob);
  const genJobRef = useRef(genJob);
  genJobRef.current = genJob;

  const createFileReferencesFromJob = useCallback(async () => {
    const currentJob = genJobRef.current;
    try {
      const measurementDateId = measurementDate?.id;
      if (!measurementDateId || !currentJob.id || !companyInfo.id) {
        return;
      }

      const page = undefined;
      const createFileReferences = true;

      await apiCompanyDocumentGenerationJobsDocumentExecutionSessionsList(
        companyInfo.id,
        `${currentJob.job_id}`,
        page,
        createFileReferences,
        measurementDateId,
        referenceObjectType as 'ct' | 'fi' | 'fo' | 'cb' | 'ev' | 'ssv' | 'fio' | 'ma' | 'rs'
      );
    } catch (error) {
      Logger.error('Error creating file references:', error);
      return [];
    }
  }, [
    apiCompanyDocumentGenerationJobsDocumentExecutionSessionsList,
    companyInfo,
    measurementDate?.id,
    referenceObjectType,
  ]);

  const handleResult = useCallback(
    async (result: RetrieveDocumentGenerationJob) => {
      const response = result;
      setGenJob(response);

      if (response.status !== RetrieveDocumentGenerationJob.status.COMPLETED) {
        return;
      }

      removeJob(response.job_id);

      try {
        await createFileReferencesFromJob();
        onJobComplete?.();
      } catch (error) {
        Logger.error('Error creating file references:', error);
        enqueueSnackbar('Error creating file references from extraction results', { variant: 'error' });
      }
    },
    [removeJob, createFileReferencesFromJob, enqueueSnackbar, onJobComplete]
  );

  useEffect(() => {
    if (skipPolling) {
      setGenJob(generationJob);
    }
  }, [skipPolling, generationJob]);

  useEffect(() => {
    if (!generationJob.id || skipPolling) return;

    addJob({
      id: `${generationJob.job_id}`,
      details: {
        description: 'AI Assisted PDF Processing',
        title: 'PDF Processing',
      },
      pollingConfig: {
        pollingInterval: 3000,
        pollingArgs: [companyInfo.id, generationJob.job_id],
        pollingFunction: apiCompanyDocumentGenerationJobsRead,
      },
      onError: err => {
        Logger.error('Error on parsing', err);
        enqueueSnackbar('Processing job completed', { variant: 'error' });
      },
      onResult: handleResult,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generationJob, skipPolling]);

  const [retriggering, setRetriggering] = useState(false);

  const handleRetriggerParsing = useCallback(async () => {
    if (!companyInfo.id) return;
    const fileIds = fileId != null ? [fileId] : [];
    if (!fileIds.length) {
      enqueueSnackbar('No reference files to reprocess', { variant: 'warning' });
      return;
    }
    setRetriggering(true);
    try {
      const newJob = await CompanyService.apiCompanyDocumentGenerationJobsNewCreate(companyInfo.id.toString(), {
        reference_files: fileIds,
        processing_type: RetrieveDocumentGenerationJob.processing_type.PARSING,
        desired_termination_state: GenerationJobState.PENDING_PROMPTING,
        parser_overrides: genJob.parser_overrides ?? [],
        metadata: genJob.metadata ?? {},
      });

      if (onRetrigger) {
        onRetrigger(newJob);
      } else {
        setGenJob(newJob);
        addJob({
          id: newJob.job_id,
          details: {
            description: 'AI Assisted PDF Processing',
            title: 'PDF Re-processing',
          },
          pollingConfig: {
            pollingInterval: 3000,
            pollingArgs: [companyInfo.id, newJob.job_id],
            pollingFunction: apiCompanyDocumentGenerationJobsRead,
          },
          onError: err => {
            Logger.error('Error on re-parsing', err);
            enqueueSnackbar('Re-processing job failed', { variant: 'error' });
          },
          onResult: handleResult,
        });
      }

      enqueueSnackbar('Re-processing job started', { variant: 'info' });
    } catch (error) {
      Logger.error('Error retriggering parsing job:', error);
      enqueueSnackbar('Failed to retrigger parsing', { variant: 'error' });
    } finally {
      setRetriggering(false);
    }
  }, [
    companyInfo.id,
    fileId,
    genJob,
    enqueueSnackbar,
    addJob,
    apiCompanyDocumentGenerationJobsRead,
    handleResult,
    onRetrigger,
  ]);

  const progress = parsingProgressState(genJob, parserReady);
  if (progress === 'ready') {
    return null;
  }

  const busy = progress === 'queued' || progress === 'parsing' || progress === 'preparing';
  return (
    <Box
      sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}
      data-testid="pdf-parsing-progress"
      data-progress-state={progress}>
      <Alert
        severity={progress === 'failed' ? 'error' : 'info'}
        icon={busy ? <CircularProgress size={20} /> : undefined}
        action={
          progress === 'failed' ? (
            <Tooltip title="Retry">
              <IconButton size="small" onClick={handleRetriggerParsing} disabled={retriggering} sx={{ ml: 1 }}>
                {retriggering ? <CircularProgress size={16} /> : <ReplayIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          ) : undefined
        }>
        {PARSING_PROGRESS_COPY[progress]}
      </Alert>
    </Box>
  );
};
